import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createShareLink,
  erasePerson,
  fetchMeeting,
  fetchMeetings,
  meetingArchiveUrl,
  mediaUrl,
  transcriptUrl,
} from './api';
import type {
  ExportFormat,
  Meeting,
  MeetingParticipant,
  MeetingTrack,
  ShareLink,
} from './api';
import { Markdown } from './Markdown';
import type { Route } from './routes';
import { hrefFor } from './routes';

/**
 * Past meetings: summary first, then what was captured, then the words.
 *
 * Summary-first is the whole point. A transcript is a primary source you consult
 * when you need the exact words; the summary is what someone catching up
 * actually reads. Burying the summary under an hour of dialogue would invert
 * that.
 *
 * The rest of this page exists to make the archive checkable rather than merely
 * browsable: which files were written, how big they are, whether the manifest
 * still verifies, and — the part that turns a transcript into a record you can
 * rely on — the ability to click a line and hear the person say it.
 */

/**
 * Participant names, defensively.
 *
 * A meeting with no participants used to arrive as `participants: null` — Go
 * marshals a nil slice that way — and mapping over it threw, blanking the entire
 * page including the list. The server sends [] now; this is the second lock,
 * because a client should not be one unexpected null away from rendering nothing
 * at all.
 */
function names(m: Meeting): string {
  return (m.participants ?? [])
    .map((p) => p.name)
    .filter(Boolean)
    .join(', ');
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function duration(m: Meeting): string {
  if (!m.endedAt) return 'in progress';
  const secs = Math.round((Date.parse(m.endedAt) - Date.parse(m.startedAt)) / 1000);
  const h = Math.floor(secs / 3600);
  const min = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
}

function bytes(n?: number): string {
  if (!n) return '';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
}

/** "mic", "cam", "screen" — the word that belongs on a chip, not the enum. */
function sourceLabel(source: string): string {
  switch (source) {
    case 'microphone':
      return 'mic';
    case 'camera':
      return 'cam';
    case 'screen_share':
      return 'screen';
    case 'screen_share_audio':
      return 'screen audio';
    default:
      return source || 'track';
  }
}

function timestamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * What stage of the pipeline this meeting has reached.
 *
 * "Nothing was said" is a STATE, not a missing summary. A meeting nobody spoke
 * in gets a transcript with no lines and a one-line stub for a summary, so a
 * marker driven by hasSummary called that finished and showed nothing — which
 * left a list where fifteen silent test joins were indistinguishable from the
 * one meeting with real content in it.
 */
export function MeetingStatus({ m }: { m: Meeting }) {
  if (!m.hasTranscript) return <span className="mx-pill">transcribing…</span>;

  // ABSENT IS NOT ZERO. A portal from before these fields existed sends
  // neither, and Go sends both without `omitempty` — so 0 means "none
  // outstanding" and `undefined` means "this server cannot tell us". Reading
  // the second as the first would put a confident "no speech" on a meeting that
  // is halfway through transcribing, which is the failure this row already
  // exists to prevent.
  const accounted = m.pending !== undefined || m.shortfall !== undefined;

  if (accounted) {
    // A fault first, and before "no speech": a meeting whose audio was lost has
    // no lines EITHER, and calling that silence is the wrong answer to give.
    if ((m.shortfall ?? 0) > 0) {
      return (
        <span
          className="mx-pill mx-pill-bad"
          title={`${m.shortfall} recorded segment(s) have no transcript and are not queued for one. This will not resolve by waiting.`}
        >
          {m.shortfall} missing
        </span>
      );
    }
    if ((m.pending ?? 0) > 0) {
      return (
        <span className="mx-pill" title="Queued audio. The count comes down on its own.">
          transcribing {m.pending}
        </span>
      );
    }
  }

  if (m.transcriptLines === 0) return <span className="mx-pill mx-pill-quiet">no speech</span>;
  if (!m.hasSummary) return <span className="mx-pill">summarising…</span>;
  return null; // the summary itself is the status
}

/**
 * Why there is no summary yet — and whether waiting will fix it.
 *
 * THE SUMMARISER NOW WAITS FOR A COMPLETE TRANSCRIPT (internal/meeting's
 * Transcript.Complete gates it), because a summary is a confident account
 * written from the lines present and nothing in it can signal that a third of
 * the conversation had not arrived. That makes the old copy here — "the
 * transcript is ready; the summary is still being written" — actively wrong in
 * the commonest case: the transcript is NOT ready, which is precisely why no
 * summary has been written.
 *
 * TWO INCOMPLETE STATES, AND THEY ARE NOT THE SAME THING:
 *
 *   `pending`   — segments are queued and WILL be transcribed. Self-healing,
 *                 worth a count, and the right answer is to wait.
 *   `shortfall` — audio the manifest says was recorded that has neither text
 *                 nor a queue entry. It is not coming. Waiting does not fix it
 *                 and the meeting will never summarise on its own.
 *
 * Reporting the second as progress would leave somebody refreshing a page
 * forever, so it is worded as the fault it is.
 *
 * Only ever rendered from a DETAIL fetch: the archive listing deliberately
 * omits the transcript body, so neither field is available on a row.
 */
export function TranscriptProgress({ m }: { m: Meeting }) {
  if (!m.hasTranscript) {
    return <p className="mx-muted">This meeting has not been transcribed yet.</p>;
  }

  const t = m.transcript;
  const pending = t?.pending?.length ?? 0;
  const shortfall = t?.shortfall ?? 0;
  const done = Object.values(t?.transcribed ?? {}).reduce((n, v) => n + v, 0);
  const total = done + pending + shortfall;

  if (shortfall > 0) {
    return (
      // `.mx-incomplete`, not `.mx-notice`: this is a defect in the record, and
      // it wears the same danger rule as an erasure rather than the accent.
      <p className="mx-incomplete">
        <b>
          {shortfall} segment{shortfall === 1 ? '' : 's'} of recorded audio {shortfall === 1 ? 'has' : 'have'} no
          transcript
        </b>{' '}
        and {shortfall === 1 ? 'is' : 'are'} not queued for one — so this will not resolve by waiting, and no
        summary will be written automatically. The lines below are what did arrive.
        {pending > 0 && ` ${pending} further segment${pending === 1 ? ' is' : 's are'} still queued.`}
      </p>
    );
  }

  if (pending > 0) {
    return (
      <p className="mx-muted">
        Still transcribing — {done} of {total} segment{total === 1 ? '' : 's'} done. The summary is written
        once every segment has arrived, so that it is not a confident account of a conversation with holes
        in it.
      </p>
    );
  }

  return (
    <p className="mx-muted">The transcript is complete; the summary is being written.</p>
  );
}

/**
 * Whether this recording still matches what the instance signed.
 *
 * FOUR STATES AND NOT A TICK. "Unsigned" is a recording from an instance with
 * no signing key and is not an accusation; "newer" means this build cannot
 * judge the document rather than that the document is wrong. Only "invalid"
 * says something went wrong, and it says it loudly.
 *
 * It is also careful about what it claims: the server checks the SIGNATURE on a
 * page load, which is microseconds. Re-hashing every byte of media is
 * `meetnix-verify`, and the tooltip says so instead of letting a green tick
 * imply the media was checked too.
 */
function SignatureBadge({ m }: { m: Meeting }) {
  const sig = m.signature;
  if (!sig) return null;
  switch (sig.state) {
    case 'valid':
      return (
        <span
          className="mx-sig ok"
          title={`Signed by this instance (${sig.key?.slice(0, 8)}…). Run meetnix-verify to re-hash the media as well.`}
        >
          Signature verified
        </span>
      );
    case 'invalid':
      return (
        <span className="mx-sig bad" title={sig.detail}>
          Signature does NOT match
        </span>
      );
    case 'newer':
      return (
        <span className="mx-sig" title={sig.detail}>
          Signature not checked
        </span>
      );
    default:
      return (
        <span className="mx-sig" title={sig.detail}>
          Unsigned
        </span>
      );
  }
}

/** A player request: which file, and where in it to start. */
interface Cue {
  file: string;
  /** Seconds into the FILE. */
  seek: number;
  label: string;
}

/**
 * The tracks, grouped by person — the archive's own version of the panel that
 * runs beside a live call.
 *
 * The counts and sizes are here because they are the evidence for the claim the
 * whole product makes: four microphones, four files, no compositing. A listing
 * that said "recording.mp4, 240 MB" would be a different product.
 */
function Tracks({
  m,
  playable,
  onPlay,
  onErase,
  canErase,
}: {
  m: Meeting;
  playable: boolean;
  onPlay: (c: Cue) => void;
  onErase: (p: MeetingParticipant) => void;
  canErase: boolean;
}) {
  const people = (m.participants ?? []).filter((p) => (p.tracks ?? []).length > 0);
  if (people.length === 0) return null;

  const all = people.flatMap((p) => p.tracks ?? []);
  const total = all.reduce((n, t) => n + (t.bytes ?? 0), 0);
  const live = all.filter((t) => !t.erased).length;

  return (
    <section className="mx-tracks">
      <div className="mx-tracks-head">
        <span>Tracks</span>
        <span>
          {live} file{live === 1 ? '' : 's'}
          {total > 0 && ` · ${bytes(total)}`}
        </span>
      </div>

      {people.map((p) => (
        <div className="mx-trk" key={p.principal}>
          <span className="mx-trk-who">
            {p.name || p.principal}
            {p.authenticatedBy && <span className="mx-trk-issuer">via {p.authenticatedBy}</span>}
          </span>
          <span className="mx-trk-files">
            {(p.tracks ?? []).map((t) => (
              <TrackChip
                key={t.file}
                track={t}
                playable={playable}
                onPlay={() =>
                  onPlay({ file: t.file, seek: 0, label: `${p.name} · ${sourceLabel(t.source)}` })
                }
              />
            ))}
          </span>
          {canErase && (
            <button
              className="mx-trk-erase"
              onClick={() => onErase(p)}
              title={`Remove ${p.name}'s media and transcript lines`}
            >
              Erase
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

function TrackChip({
  track,
  playable,
  onPlay,
}: {
  track: MeetingTrack;
  playable: boolean;
  onPlay: () => void;
}) {
  if (track.erased) {
    return (
      <span className="mx-trk-chip erased" title="Removed by a deletion request">
        {sourceLabel(track.source)} · erased
      </span>
    );
  }
  const label = `${sourceLabel(track.source)}${track.bytes ? ` · ${bytes(track.bytes)}` : ''}`;

  // AUDIO PLAYS, VIDEO DOES NOT, and the UI should not pretend otherwise. The
  // recorder writes what arrived — Opus into Ogg, VP8 into IVF, H.264 raw — with
  // no re-encode, which is the property the archive is FOR. Ogg/Opus is
  // something every browser plays; IVF is not something any browser plays. So
  // video is offered as a download and labelled, rather than wired to a player
  // that would show a black rectangle.
  if (!playable || track.kind !== 'audio') {
    return (
      <span className="mx-trk-chip" title={track.sha256 ? `sha256 ${track.sha256}` : undefined}>
        {label}
      </span>
    );
  }
  return (
    <button
      className="mx-trk-chip play"
      onClick={onPlay}
      title={track.sha256 ? `sha256 ${track.sha256}` : undefined}
    >
      ▸ {label}
    </button>
  );
}

/** Export the transcript in a format some other tool takes. */
function ExportMenu({ m }: { m: Meeting }) {
  const formats: { f: ExportFormat; label: string }[] = [
    { f: 'md', label: 'Markdown minutes' },
    { f: 'vtt', label: 'WebVTT' },
    { f: 'srt', label: 'SubRip' },
    { f: 'json', label: 'JSON' },
  ];
  return (
    <details className="mx-export">
      <summary className="mx-chip">Export transcript</summary>
      <div className="mx-export-menu">
        {formats.map(({ f, label }) => (
          <a key={f} href={transcriptUrl(m.room, m.session, f)} download>
            {label}
          </a>
        ))}
      </div>
    </details>
  );
}

/**
 * "Share with a guest" — mints a link to THIS meeting's written record and puts
 * it on the clipboard.
 *
 * Operators only, and hidden rather than disabled for everyone else: the portal
 * refuses a non-operator anyway, and a button that always errors is worse than
 * no button. Handing a link out is a disclosure decision, which is why it sits
 * where the raw media does rather than with the staff-wide read.
 *
 * Mirrors InviteButton deliberately, including showing the URL as selectable
 * text: clipboard writes fail silently in plenty of situations (permissions,
 * non-secure context, some mobile browsers), and "I clicked it and nothing
 * happened" is a bad outcome for the one feature whose whole job is to hand
 * someone a URL.
 *
 * This is a SUPPLEMENT to the summary mail, which issues a link per recipient
 * automatically. It is what you reach for when somebody was not in the meeting,
 * when an address was wrong, or when the relay is not wired up yet.
 */
function ShareButton({ m, isOp }: { m: Meeting; isOp?: boolean }) {
  const [link, setLink] = useState<ShareLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const mint = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createShareLink(m.room, m.session);
      setLink(created);
      try {
        await navigator.clipboard.writeText(created.url);
        setCopied(true);
      } catch {
        setCopied(false); // the URL is on screen regardless
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [m.room, m.session]);

  if (!isOp) return null;

  return (
    <div className="mx-invite">
      <button className="mx-chip" onClick={() => void mint()} disabled={busy}>
        {busy ? 'Creating…' : 'Share with a guest'}
      </button>

      {(link || error) && (
        <div className="mx-invite-pop" role="status">
          {error && <p className="mx-error">{error}</p>}
          {link && (
            <>
              <p className="mx-muted">
                {copied ? 'Link copied to your clipboard.' : 'Copy this link and send it:'}
              </p>
              <input
                className="mx-invite-url"
                readOnly
                value={link.url}
                onFocus={(e) => e.currentTarget.select()}
              />
              <p className="mx-muted">
                Expires {new Date(link.expires).toLocaleString()}. It opens the summary and
                transcript of this meeting only — not the audio or video, and not the exports —
                and it works without an account, so treat it as the thing you are disclosing.
              </p>
              <button className="mx-invite-close" onClick={() => setLink(null)}>
                Done
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function MeetingDetail({
  room,
  session,
  token,
  at,
  isOp,
  onBack,
}: {
  room: string;
  session: string;
  /** A guest's signed link. Present only when they arrived from their email. */
  token?: string;
  /** Seconds to open at, from a search result's citation. */
  at?: number;
  isOp?: boolean;
  /** Absent for a guest: there is no list for them to go back to. */
  onBack?: () => void;
}) {
  const [m, setM] = useState<Meeting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(at !== undefined);
  const [cue, setCue] = useState<Cue | null>(null);
  const [erasing, setErasing] = useState<MeetingParticipant | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const audio = useRef<HTMLAudioElement>(null);
  const seekTo = useRef<number>(0);

  const load = useCallback(() => {
    fetchMeeting(room, session, token)
      .then(setM)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [room, session, token]);

  useEffect(load, [load]);

  // Memoised because it is an effect dependency: `?? []` is a fresh array on
  // every render, which would re-run the citation effect on every render.
  const lines = useMemo(() => m?.transcript?.lines ?? [], [m]);

  /**
   * Find the audio a line was said on, and where in that file to start.
   *
   * The line's time is on the MEETING's clock; the file's zero is when that
   * person's track began. Subtracting the offset is what makes clicking a line
   * play the sentence rather than something eleven minutes away from it.
   */
  const play = useCallback(
    (principal: string | undefined, speaker: string, tMeeting: number, stem?: string) => {
      if (!m?.playable) return;
      const person =
        (m.participants ?? []).find((p) => principal && p.principal === principal) ??
        (m.participants ?? []).find((p) => p.name === speaker);
      const audio = (person?.tracks ?? []).filter((t) => t.kind === 'audio' && !t.erased);
      // The track the line was heard on, when the line says (MN-16): somebody
      // who rejoined has one file per join, and the first is not the one
      // holding a sentence from after the rejoin. Older lines fall back to it.
      const track =
        (stem && audio.find((t) => t.file.replace(/\.[^.]+$/, '') === stem)) || audio[0];
      if (!track) {
        setNotice(
          `There is no audio for ${speaker} in this meeting — it may have been erased, or they joined without a microphone.`,
        );
        return;
      }
      setNotice(null);
      setCue({
        file: track.file,
        seek: Math.max(0, tMeeting - (track.offset ?? 0)),
        label: `${person?.name ?? speaker} · ${timestamp(tMeeting)}`,
      });
    },
    [m],
  );

  // A citation from search: open the transcript at that second and start there.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || at === undefined || !m || lines.length === 0) return;
    opened.current = true;
    const line = lines.reduce((best, l) => (l.t <= at + 0.5 && l.t > best.t ? l : best), lines[0]!);
    play(line.principal, line.speaker, line.t, line.track);
    // Scroll it into view once the transcript is open.
    requestAnimationFrame(() => {
      document.querySelector('.mx-line.cited')?.scrollIntoView({ block: 'center' });
    });
  }, [at, m, lines, play]);

  // Load the file, THEN seek. Setting currentTime before the metadata arrives
  // is silently ignored by every browser, which reads as "the player ignores
  // where I clicked".
  useEffect(() => {
    const el = audio.current;
    if (!el || !cue || !m) return;
    seekTo.current = cue.seek;
    el.src = mediaUrl(m.room, m.session, cue.file);
    el.load();
  }, [cue, m]);

  const doErase = useCallback(
    async (p: MeetingParticipant, reason: string) => {
      try {
        const got = await erasePerson(room, session, p.principal, reason);
        setErasing(null);
        setNotice(
          `Removed ${got.tracks.length} track(s) and ${got.transcript_lines} transcript line(s) for ${p.name}. They remain in the participant list, and the removal is recorded in the manifest.`,
        );
        load();
      } catch (e) {
        setNotice(e instanceof Error ? e.message : String(e));
      }
    },
    [room, session, load],
  );

  const back = onBack ? (
    <button className="mx-back" onClick={onBack}>
      ← All meetings
    </button>
  ) : null;

  if (error) {
    return (
      <>
        {back}
        <p className="mx-error">{error}</p>
        <p className="mx-muted">
          {/* The most likely person to land here is a guest whose link has run
              out — say that, rather than implying the meeting was deleted or
              that they should have an account. */}
          {error.includes('expired')
            ? 'Ask whoever ran the meeting to send you a fresh link.'
            : 'That meeting may have been removed, or the link may be for a different deployment.'}
        </p>
      </>
    );
  }
  if (!m) return <p className="mx-muted">Loading…</p>;

  const cited = at !== undefined ? at : null;

  return (
    <article className="mx-meeting">
      {back}
      <h1>{m.title || m.room}</h1>
      <p className="mx-muted">
        {m.title && (
          <>
            <span className="mx-meeting-room">{m.room}</span>
            <span className="mx-dotsep">·</span>
          </>
        )}
        {when(m.startedAt)} <span className="mx-dotsep">·</span> {duration(m)}
        {names(m) && (
          <>
            {' '}
            <span className="mx-dotsep">·</span> {names(m)}
          </>
        )}
      </p>

      <div className="mx-meeting-actions">
        {/* Watch it back like a video (MN-35). Only for whoever may play the
            media; the player checks again, and so does the server. */}
        {m.playable && (
          <a
            className="mx-button mx-play-link"
            href={hrefFor({ name: 'play', room: m.room, session: m.session, token })}
          >
            ▶ Play back
          </a>
        )}
        <SignatureBadge m={m} />
        {(m.held ?? []).length > 0 && (
          <span
            className="mx-sig held"
            title="Held material ignores the retention policy, and an erasure against it is refused."
          >
            Legal hold · {(m.held ?? []).join(', ')}
          </span>
        )}
        <ExportMenu m={m} />
        <ShareButton m={m} isOp={isOp} />
        {isOp && (
          <a className="mx-chip" href={meetingArchiveUrl(m.room, m.session)} download>
            Download all
          </a>
        )}
      </div>

      {notice && <p className="mx-notice">{notice}</p>}

      {(m.erasures ?? []).map((e, i) => (
        <p className="mx-erasure" key={i}>
          <b>{e.name || e.principal}</b>&rsquo;s media was erased on{' '}
          {new Date(e.at).toLocaleDateString()} by {e.by}
          {e.reason && ` — ${e.reason}`}. {e.tracks.length} track(s) and {e.transcript_lines}{' '}
          transcript line(s) were removed; they remain in the participant list.
        </p>
      ))}

      {/* One player for the whole page. Which track it holds is decided by what
          you clicked — a track chip, or a line in the transcript. */}
      {m.playable && (
        <div className={`mx-player${cue ? '' : ' idle'}`}>
          <audio
            ref={audio}
            controls
            preload="none"
            onLoadedMetadata={(e) => {
              const el = e.currentTarget;
              el.currentTime = seekTo.current;
              void el.play().catch(() => {
                /* autoplay refused: the controls are right there */
              });
            }}
          />
          <span className="mx-player-label">
            {cue ? cue.label : 'Pick a track, or click a line in the transcript'}
          </span>
        </div>
      )}

      {m.summary ? <Markdown text={m.summary} /> : <TranscriptProgress m={m} />}

      <Tracks
        m={m}
        playable={!!m.playable}
        onPlay={setCue}
        canErase={!!isOp}
        onErase={setErasing}
      />

      {erasing && (
        <EraseDialog
          person={erasing}
          held={m.held ?? []}
          onCancel={() => setErasing(null)}
          onConfirm={(reason) => void doErase(erasing, reason)}
        />
      )}

      {lines.length > 0 && (
        <section className="mx-transcript">
          <button className="mx-disclose" onClick={() => setShowTranscript((v) => !v)}>
            {showTranscript ? '▾' : '▸'} Full transcript ({lines.length} lines)
          </button>
          {showTranscript && (
            <ol>
              {lines.map((l, i) => {
                const isCited = cited !== null && Math.abs(l.t - cited) < 0.5;
                return (
                  <li
                    key={i}
                    className={`mx-line${isCited ? ' cited' : ''}${
                      m.playable ? ' playable' : ''
                    }`}
                    onClick={m.playable ? () => play(l.principal, l.speaker, l.t, l.track) : undefined}
                    title={m.playable ? 'Play this line' : undefined}
                  >
                    <span className="mx-ts">{timestamp(l.t)}</span>
                    <span className="mx-speaker">{l.speaker}</span>
                    <span>{l.text}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      )}
    </article>
  );
}

/**
 * Confirming an erasure.
 *
 * It states what will and will NOT happen before asking, because the two are
 * equally load-bearing and an operator carrying out a deletion request has to
 * be able to answer both. The reason is asked for rather than optional-looking:
 * an erasure with nothing recorded about why is the one a hostile reader
 * assumes the worst about.
 */
function EraseDialog({
  person,
  held,
  onCancel,
  onConfirm,
}: {
  person: MeetingParticipant;
  held: string[];
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="mx-erase-dialog">
      <h3>Erase {person.name}&rsquo;s media from this meeting?</h3>
      {/* Said BEFORE the button rather than after the refusal. The server will
          refuse this anyway, but an operator carrying out a deletion request
          needs to know it will be refused while they are still talking to the
          person who asked — not afterwards. */}
      {held.length > 0 && (
        <p className="mx-error">
          This meeting is under legal hold ({held.join(', ')}), so this will be refused. A legal
          obligation to retain outlives the right to erasure for as long as it lasts; lift the
          hold first, or record why the request cannot be met yet.
        </p>
      )}
      <ul>
        <li>Their track files are deleted and cannot be recovered from here.</li>
        <li>Their transcript lines are removed, and the summary is regenerated without them.</li>
        <li>
          They <b>stay</b> in the participant list, and the erasure is recorded in the manifest —
          so the record never quietly claims they were not in the room.
        </li>
      </ul>
      <label>
        Why, for the record
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="subject request, ticket number, …"
          autoFocus
        />
      </label>
      <div className="mx-erase-actions">
        <button className="mx-invite-btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="mx-button mx-button-inline danger" onClick={() => onConfirm(reason)}>
          Erase the media
        </button>
      </div>
    </div>
  );
}

/**
 * The meeting archive.
 *
 * Which meeting is open comes from the ROUTE, not from internal state: a summary
 * email links straight at one, and that link has to work whether it is followed
 * cold, followed after a sign-in redirect, or reached by the back button.
 */
export function Meetings({
  open,
  isOp,
  navigate,
}: {
  open: { room: string; session: string; token?: string; at?: number } | null;
  isOp?: boolean;
  navigate: (r: Route) => void;
}) {
  const [list, setList] = useState<Meeting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * "Some way to navigate their stuff" — the ask this switch answers.
   *
   * Defaults to MINE. On a shared deployment the archive is mostly other
   * people's meetings, and somebody looking for what they said last Tuesday
   * should not have to scroll past them. Everything is one click away, and the
   * counts in the labels say how much is behind each so the switch is not a
   * guess.
   */
  const [mine, setMine] = useState(true);
  const [total, setTotal] = useState<number | null>(null);

  const load = useCallback(() => {
    fetchMeetings(mine)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [mine]);

  useEffect(load, [load]);

  // The other tab's count, so the switch can say what is behind it. Fetched
  // once rather than on every toggle — it is a number on a label, and a second
  // archive walk per click is not worth it.
  useEffect(() => {
    fetchMeetings(false)
      .then((all) => setTotal(all.length))
      .catch(() => setTotal(null));
  }, []);

  // The frame supplies the bar, the nav and the account menu (AppShell); this
  // page is its own content only.
  return (
    <>
      {!open && (
        <div className="mx-scope">
          <button
            className={`mx-chip${mine ? ' on' : ''}`}
            aria-pressed={mine}
            onClick={() => setMine(true)}
          >
            Mine
          </button>
          <button
            className={`mx-chip${mine ? '' : ' on'}`}
            aria-pressed={!mine}
            onClick={() => setMine(false)}
          >
            Everything{total !== null ? ` (${total})` : ''}
          </button>
        </div>
      )}

      {open ? (
        <MeetingDetail
          room={open.room}
          session={open.session}
          token={open.token}
          at={open.at}
          isOp={isOp}
          onBack={() => navigate({ name: 'meetings' })}
        />
      ) : error ? (
        <p className="mx-error">{error}</p>
      ) : !list ? (
        <p className="mx-muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="mx-muted">
          {mine
            ? 'You have not been in a recorded meeting yet. Meetings other people held are under Everything.'
            : 'No meetings have been recorded yet.'}
        </p>
      ) : (
        <MeetingList list={list} navigate={navigate} />
      )}
    </>
  );
}

/** The list itself, shared with the dashboard's "other meetings" panel. */
export function MeetingList({
  list,
  navigate,
}: {
  list: Meeting[];
  navigate: (r: Route) => void;
}) {
  const rows = useMemo(() => list, [list]);
  return (
    <ul className="mx-meeting-list">
      {rows.map((m) => (
        <li key={`${m.room}/${m.session}`}>
          <button onClick={() => navigate({ name: 'meeting', room: m.room, session: m.session })}>
            <span className="mx-meeting-when">{when(m.startedAt)}</span>
            <span className="mx-meeting-who">
              {m.title ? (
                <>
                  <b>{m.title}</b> <span className="mx-muted">{names(m)}</span>
                </>
              ) : (
                names(m) || 'nobody recorded'
              )}
            </span>
            <span className="mx-meeting-meta">
              {duration(m)} <MeetingStatus m={m} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
